export type PipelineZone = 'mainLine' | 'branchLine' | 'inEngineRoom';

export interface PipelineFieldDef {
  key: string;
  label: string;
  snakeKey: string;
}

export interface PipelineSegmentDefaults {
  thickness: Record<PipelineZone, number[]>;
  length: Record<PipelineZone, number[]>;
}

export interface PipelineDiameterReferenceRow {
  nominalDiameterA: number;
  insideDiameterD1: number;
  outsideDiameterD2: number;
}

export interface PipelineSegmentProfile {
  defaults: PipelineSegmentDefaults;
  /** When false, only thickness values are auto-populated. Defaults to true. */
  autoPopulateLengths?: boolean;
  scheduleNote?: string;
  referenceDiameters?: Partial<Record<PipelineZone, PipelineDiameterReferenceRow[]>>;
}

const SCHEDULE_40_PIPELINE_NOTE =
  'All pipelines are considered as per Schedule 40, which is generally used in ship heating lines.';

export const MR_PIPELINE_DEFAULTS: PipelineSegmentDefaults = {
  thickness: {
    mainLine: [16.4, 14.2, 11],
    branchLine: [12, 11, 10.3],
    inEngineRoom: [18.5],
  },
  length: {
    mainLine: [83.52, 20.88, 20.88],
    branchLine: [40, 8, 48],
    inEngineRoom: [29.67],
  },
};

/** HANDY uses the same pipeline defaults as MR. */
export const HANDY_PIPELINE_DEFAULTS: PipelineSegmentDefaults = MR_PIPELINE_DEFAULTS;

export const INTERMEDIATE_PIPELINE_DEFAULTS: PipelineSegmentDefaults = {
  thickness: {
    mainLine: [13, 12, 7.8],
    branchLine: [10.3, 7.8, 7.4],
    inEngineRoom: [14.2],
  },
  length: {
    mainLine: [],
    branchLine: [],
    inEngineRoom: [],
  },
};

const INTERMEDIATE_REFERENCE_DIAMETERS: Partial<
  Record<PipelineZone, PipelineDiameterReferenceRow[]>
> = {
  mainLine: [
    { nominalDiameterA: 125, insideDiameterD1: 128.3, outsideDiameterD2: 141.3 },
    { nominalDiameterA: 100, insideDiameterD1: 102.3, outsideDiameterD2: 114.3 },
    { nominalDiameterA: 50, insideDiameterD1: 52.5, outsideDiameterD2: 60.3 },
  ],
  branchLine: [
    { nominalDiameterA: 65, insideDiameterD1: 62.7, outsideDiameterD2: 73 },
    { nominalDiameterA: 50, insideDiameterD1: 52.5, outsideDiameterD2: 60.3 },
    { nominalDiameterA: 40, insideDiameterD1: 40.9, outsideDiameterD2: 48.3 },
  ],
  inEngineRoom: [
    { nominalDiameterA: 150, insideDiameterD1: 154.1, outsideDiameterD2: 168.3 },
  ],
};

export const LR1_PIPELINE_DEFAULTS: PipelineSegmentDefaults = {
  thickness: {
    mainLine: [18.5, 16.4, 12],
    branchLine: [13, 12, 11],
    inEngineRoom: [20.7],
  },
  length: {
    mainLine: [104.4, 26.1, 26.1],
    branchLine: [40, 8, 48],
    inEngineRoom: [37.1],
  },
};

const LR1_REFERENCE_DIAMETERS: Partial<
  Record<PipelineZone, PipelineDiameterReferenceRow[]>
> = {
  mainLine: [
    { nominalDiameterA: 250, insideDiameterD1: 254.5, outsideDiameterD2: 273 },
    { nominalDiameterA: 200, insideDiameterD1: 202.7, outsideDiameterD2: 219.1 },
    { nominalDiameterA: 100, insideDiameterD1: 102.3, outsideDiameterD2: 114.3 },
  ],
  branchLine: [
    { nominalDiameterA: 125, insideDiameterD1: 128.3, outsideDiameterD2: 141.3 },
    { nominalDiameterA: 100, insideDiameterD1: 102.3, outsideDiameterD2: 114.3 },
    { nominalDiameterA: 80, insideDiameterD1: 77.9, outsideDiameterD2: 88.9 },
  ],
  inEngineRoom: [
    { nominalDiameterA: 300, insideDiameterD1: 303.2, outsideDiameterD2: 323.9 },
  ],
};

export const AFRAMAX_PIPELINE_DEFAULTS: PipelineSegmentDefaults = {
  thickness: {
    mainLine: [18.5, 16.4, 12],
    branchLine: [13, 12, 11],
    inEngineRoom: [20.7],
  },
  length: {
    mainLine: [113.5872, 28.3968, 28.3968],
    branchLine: [54.4, 10.88, 65.28],
    inEngineRoom: [40.3512],
  },
};

export const SUEZMAX_PIPELINE_DEFAULTS: PipelineSegmentDefaults = {
  thickness: {
    mainLine: [20.7, 18.5, 16.4],
    branchLine: [14.2, 13, 12],
    inEngineRoom: [22.3],
  },
  length: {
    mainLine: [124.4448, 31.1112, 31.1112],
    branchLine: [56.8, 11.36, 68.16],
    inEngineRoom: [44.2083],
  },
};

const SUEZMAX_REFERENCE_DIAMETERS: Partial<
  Record<PipelineZone, PipelineDiameterReferenceRow[]>
> = {
  mainLine: [
    { nominalDiameterA: 300, insideDiameterD1: 303.2, outsideDiameterD2: 323.9 },
    { nominalDiameterA: 250, insideDiameterD1: 254.5, outsideDiameterD2: 273 },
    { nominalDiameterA: 200, insideDiameterD1: 202.7, outsideDiameterD2: 219.1 },
  ],
  branchLine: [
    { nominalDiameterA: 150, insideDiameterD1: 154.1, outsideDiameterD2: 168.3 },
    { nominalDiameterA: 125, insideDiameterD1: 128.3, outsideDiameterD2: 141.3 },
    { nominalDiameterA: 100, insideDiameterD1: 102.3, outsideDiameterD2: 114.3 },
  ],
  inEngineRoom: [
    { nominalDiameterA: 350, insideDiameterD1: 333.3, outsideDiameterD2: 355.6 },
  ],
};

export const VLCC_PIPELINE_DEFAULTS: PipelineSegmentDefaults = {
  thickness: {
    mainLine: [22.3, 20.7, 18.5],
    branchLine: [16.4, 14.2, 12],
    inEngineRoom: [25.4],
  },
  length: {
    mainLine: [152, 38, 38],
    branchLine: [93, 18.6, 111.6],
    inEngineRoom: [55.1862],
  },
};

const VLCC_REFERENCE_DIAMETERS: Partial<
  Record<PipelineZone, PipelineDiameterReferenceRow[]>
> = {
  mainLine: [
    { nominalDiameterA: 350, insideDiameterD1: 333.3, outsideDiameterD2: 355.6 },
    { nominalDiameterA: 300, insideDiameterD1: 303.2, outsideDiameterD2: 323.9 },
    { nominalDiameterA: 250, insideDiameterD1: 254.5, outsideDiameterD2: 273 },
  ],
  branchLine: [
    { nominalDiameterA: 200, insideDiameterD1: 202.7, outsideDiameterD2: 219.1 },
    { nominalDiameterA: 150, insideDiameterD1: 154.1, outsideDiameterD2: 168.3 },
    { nominalDiameterA: 100, insideDiameterD1: 102.3, outsideDiameterD2: 114.3 },
  ],
  inEngineRoom: [
    { nominalDiameterA: 400, insideDiameterD1: 381, outsideDiameterD2: 406.4 },
  ],
};

/** Registry for segment-based pipeline profiles — add new segments here. */
export const PIPELINE_SEGMENT_PROFILES: Record<string, PipelineSegmentProfile> = {
  MR: { defaults: MR_PIPELINE_DEFAULTS },
  HANDY: {
    defaults: HANDY_PIPELINE_DEFAULTS,
    scheduleNote: SCHEDULE_40_PIPELINE_NOTE,
  },
  INTERMEDIATE: {
    defaults: INTERMEDIATE_PIPELINE_DEFAULTS,
    autoPopulateLengths: false,
    scheduleNote: SCHEDULE_40_PIPELINE_NOTE,
    referenceDiameters: INTERMEDIATE_REFERENCE_DIAMETERS,
  },
  LR1: {
    defaults: LR1_PIPELINE_DEFAULTS,
    scheduleNote: SCHEDULE_40_PIPELINE_NOTE,
    referenceDiameters: LR1_REFERENCE_DIAMETERS,
  },
  AFRAMAX: {
    defaults: AFRAMAX_PIPELINE_DEFAULTS,
    scheduleNote: SCHEDULE_40_PIPELINE_NOTE,
    referenceDiameters: LR1_REFERENCE_DIAMETERS,
  },
  SUEZMAX: {
    defaults: SUEZMAX_PIPELINE_DEFAULTS,
    scheduleNote: SCHEDULE_40_PIPELINE_NOTE,
    referenceDiameters: SUEZMAX_REFERENCE_DIAMETERS,
  },
  VLCC: {
    defaults: VLCC_PIPELINE_DEFAULTS,
    scheduleNote: SCHEDULE_40_PIPELINE_NOTE,
    referenceDiameters: VLCC_REFERENCE_DIAMETERS,
  },
};

/** @deprecated Use PIPELINE_SEGMENT_PROFILES */
export const PIPELINE_SEGMENT_DEFAULTS: Record<string, PipelineSegmentDefaults> =
  Object.fromEntries(
    Object.entries(PIPELINE_SEGMENT_PROFILES).map(([key, profile]) => [
      key,
      profile.defaults,
    ]),
  );

export const PIPELINE_SEGMENT_SCHEDULE_NOTES: Record<string, string> =
  Object.fromEntries(
    Object.entries(PIPELINE_SEGMENT_PROFILES)
      .filter(([, profile]) => profile.scheduleNote)
      .map(([key, profile]) => [key, profile.scheduleNote!]),
  );

export const PIPELINE_ZONE_LABELS: Record<PipelineZone, string> = {
  mainLine: 'Main Line',
  branchLine: 'Branch Line',
  inEngineRoom: 'Engine Room',
};

export const PIPELINE_THICKNESS_FIELDS: Record<PipelineZone, PipelineFieldDef[]> =
  {
    mainLine: [
      {
        key: 'pipelineMainLineThickness1Mm',
        label: 'Thickness 1',
        snakeKey: 'pipeline_main_line_thickness_1_mm',
      },
      {
        key: 'pipelineMainLineThickness2Mm',
        label: 'Thickness 2',
        snakeKey: 'pipeline_main_line_thickness_2_mm',
      },
      {
        key: 'pipelineMainLineThickness3Mm',
        label: 'Thickness 3',
        snakeKey: 'pipeline_main_line_thickness_3_mm',
      },
    ],
    branchLine: [
      {
        key: 'pipelineBranchLineThickness1Mm',
        label: 'Thickness 1',
        snakeKey: 'pipeline_branch_line_thickness_1_mm',
      },
      {
        key: 'pipelineBranchLineThickness2Mm',
        label: 'Thickness 2',
        snakeKey: 'pipeline_branch_line_thickness_2_mm',
      },
      {
        key: 'pipelineBranchLineThickness3Mm',
        label: 'Thickness 3',
        snakeKey: 'pipeline_branch_line_thickness_3_mm',
      },
    ],
    inEngineRoom: [
      {
        key: 'pipelineInEngineRoomThicknessMm',
        label: 'Thickness',
        snakeKey: 'pipeline_in_engine_room_thickness_mm',
      },
    ],
  };

export const PIPELINE_LENGTH_FIELDS: Record<PipelineZone, PipelineFieldDef[]> = {
  mainLine: [
    {
      key: 'pipelineMainLineLength1M',
      label: 'Length 1',
      snakeKey: 'pipeline_main_line_length_1_m',
    },
    {
      key: 'pipelineMainLineLength2M',
      label: 'Length 2',
      snakeKey: 'pipeline_main_line_length_2_m',
    },
    {
      key: 'pipelineMainLineLength3M',
      label: 'Length 3',
      snakeKey: 'pipeline_main_line_length_3_m',
    },
  ],
  branchLine: [
    {
      key: 'pipelineBranchLineLength1M',
      label: 'Length 1',
      snakeKey: 'pipeline_branch_line_length_1_m',
    },
    {
      key: 'pipelineBranchLineLength2M',
      label: 'Length 2',
      snakeKey: 'pipeline_branch_line_length_2_m',
    },
    {
      key: 'pipelineBranchLineLength3M',
      label: 'Length 3',
      snakeKey: 'pipeline_branch_line_length_3_m',
    },
  ],
  inEngineRoom: [
    {
      key: 'pipelineInEngineRoomLengthM',
      label: 'Length',
      snakeKey: 'pipeline_in_engine_room_length_m',
    },
  ],
};

export const ALL_PIPELINE_DETAIL_FIELD_KEYS: string[] = [
  ...Object.values(PIPELINE_THICKNESS_FIELDS).flatMap((fields) =>
    fields.map((field) => field.key),
  ),
  ...Object.values(PIPELINE_LENGTH_FIELDS).flatMap((fields) =>
    fields.map((field) => field.key),
  ),
];

export const PIPELINE_FIELD_SNAKE_MAP: Record<string, string> = [
  ...Object.values(PIPELINE_THICKNESS_FIELDS).flat(),
  ...Object.values(PIPELINE_LENGTH_FIELDS).flat(),
].reduce<Record<string, string>>((acc, field) => {
  acc[field.key] = field.snakeKey;
  return acc;
}, {});

const PIPELINE_ZONE_SECTION_NAMES: Record<PipelineZone, string> = {
  mainLine: 'Main Line',
  branchLine: 'Branch Line',
  inEngineRoom: 'In Engine Room',
};

const LEGACY_PIPELINE_FIELD_MAP: Record<string, string> = {
  pipelineMainLineThicknessMm: 'pipelineMainLineThickness1Mm',
  pipelineBranchLineThicknessMm: 'pipelineBranchLineThickness1Mm',
  pipelineMainLineLengthM: 'pipelineMainLineLength1M',
  pipelineBranchLineLengthM: 'pipelineBranchLineLength1M',
};

export function normalizePipelineSegment(segment: unknown): string {
  const raw = String(segment ?? '').trim();
  if (!raw) {
    return '';
  }
  const upper = raw.toUpperCase();
  if (upper.startsWith('HANDY')) {
    return 'HANDY';
  }
  if (/^MR\d*$/.test(upper)) {
    return 'MR';
  }
  return upper;
}

export function getPipelineSegmentScheduleNote(segment: unknown): string | null {
  const key = normalizePipelineSegment(segment);
  return PIPELINE_SEGMENT_SCHEDULE_NOTES[key] ?? null;
}

export function getPipelineSegmentProfile(
  segment: unknown,
): PipelineSegmentProfile | null {
  const key = normalizePipelineSegment(segment);
  return PIPELINE_SEGMENT_PROFILES[key] ?? null;
}

export function getPipelineDefaultsForSegment(
  segment: unknown,
): PipelineSegmentDefaults | null {
  return getPipelineSegmentProfile(segment)?.defaults ?? null;
}

export function getPipelineReferenceDiameters(
  segment: unknown,
  zone: PipelineZone,
): PipelineDiameterReferenceRow[] {
  const rows = getPipelineSegmentProfile(segment)?.referenceDiameters?.[zone];
  return rows?.length ? rows : [];
}

export function buildPipelineDefaultsFormPatch(
  segment: unknown,
): Record<string, number> | null {
  const profile = getPipelineSegmentProfile(segment);
  if (!profile) {
    return null;
  }

  const patch: Record<string, number> = {};
  const { defaults } = profile;
  const populateLengths = profile.autoPopulateLengths !== false;

  (Object.keys(PIPELINE_THICKNESS_FIELDS) as PipelineZone[]).forEach((zone) => {
    const fields = PIPELINE_THICKNESS_FIELDS[zone];
    const values = defaults.thickness[zone];
    fields.forEach((field, index) => {
      const value = values[index];
      if (value != null) {
        patch[field.key] = value;
      }
    });
  });

  if (populateLengths) {
    (Object.keys(PIPELINE_LENGTH_FIELDS) as PipelineZone[]).forEach((zone) => {
      const fields = PIPELINE_LENGTH_FIELDS[zone];
      const values = defaults.length[zone];
      fields.forEach((field, index) => {
        const value = values[index];
        if (value != null) {
          patch[field.key] = value;
        }
      });
    });
  }

  return Object.keys(patch).length ? patch : null;
}

export function pipelineFieldsAreEmpty(
  formValue: Record<string, unknown>,
): boolean {
  return ALL_PIPELINE_DETAIL_FIELD_KEYS.every((key) => {
    const value = formValue[key];
    return value === null || value === undefined || value === '';
  });
}

export function pipelineFieldHasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

const ALL_PIPELINE_THICKNESS_FIELD_KEYS = Object.values(
  PIPELINE_THICKNESS_FIELDS,
).flatMap((fields) => fields.map((field) => field.key));

const ALL_PIPELINE_LENGTH_FIELD_KEYS = Object.values(
  PIPELINE_LENGTH_FIELDS,
).flatMap((fields) => fields.map((field) => field.key));

/**
 * Apply segment defaults for any thickness/length group that is not fully populated.
 * Handles MR1/MR2 → MR and legacy single-field restores that block full defaults.
 */
export function buildSegmentPipelineDefaultsPatch(
  segment: unknown,
  formValue: Record<string, unknown>,
): Record<string, number> | null {
  const profile = getPipelineSegmentProfile(segment);
  const defaults = buildPipelineDefaultsFormPatch(segment);
  if (!profile || !defaults) {
    return null;
  }

  const patch: Record<string, number> = {};
  const thicknessComplete = ALL_PIPELINE_THICKNESS_FIELD_KEYS.every((key) =>
    pipelineFieldHasValue(formValue[key]),
  );
  if (!thicknessComplete) {
    ALL_PIPELINE_THICKNESS_FIELD_KEYS.forEach((key) => {
      if (defaults[key] != null) {
        patch[key] = defaults[key];
      }
    });
  }

  if (profile.autoPopulateLengths !== false) {
    const lengthComplete = ALL_PIPELINE_LENGTH_FIELD_KEYS.every((key) =>
      pipelineFieldHasValue(formValue[key]),
    );
    if (!lengthComplete) {
      ALL_PIPELINE_LENGTH_FIELD_KEYS.forEach((key) => {
        if (defaults[key] != null) {
          patch[key] = defaults[key];
        }
      });
    }
  }

  return Object.keys(patch).length ? patch : null;
}

function toOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const cleaned = String(value).replace(/,/g, '').replace(/[^\d.-]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePipelineValueList(
  value: unknown,
  expectedCount: number,
): Array<number | null> {
  if (Array.isArray(value)) {
    return value
      .slice(0, expectedCount)
      .map((entry) => toOptionalNumber(entry));
  }
  const single = toOptionalNumber(value);
  if (single == null) {
    return Array.from({ length: expectedCount }, () => null);
  }
  const values = Array.from({ length: expectedCount }, () => null as number | null);
  values[0] = single;
  return values;
}

export function serializePipelineSectionsFromForm(
  formValue: Record<string, unknown>,
): Array<Record<string, unknown>> {
  return (Object.keys(PIPELINE_ZONE_SECTION_NAMES) as PipelineZone[]).map(
    (zone) => {
      const thicknessValues = PIPELINE_THICKNESS_FIELDS[zone].map((field) =>
        toOptionalNumber(formValue[field.key]),
      );
      const lengthValues = PIPELINE_LENGTH_FIELDS[zone].map((field) =>
        toOptionalNumber(formValue[field.key]),
      );

      return {
        segment_name: PIPELINE_ZONE_SECTION_NAMES[zone],
        pipeline_thickness_mm: thicknessValues,
        length_m: lengthValues,
        pipeline_thickness_values_mm: thicknessValues,
        length_values_m: lengthValues,
        steam_insulation:
          formValue[
            zone === 'mainLine'
              ? 'steamInsulationMainLine'
              : zone === 'branchLine'
                ? 'steamInsulationBranchLine'
                : 'steamInsulationInEngineRoom'
          ] ?? 'Yes',
      };
    },
  );
}

export function restorePipelineFormPatch(
  saved: Record<string, unknown>,
): Record<string, unknown> | null {
  const patch: Record<string, unknown> = {};
  let restored = false;

  const sections = saved['pipelineSections'] ?? saved['pipeline_sections'];
  if (Array.isArray(sections) && sections.length) {
    const byName = (name: string) =>
      (sections as Record<string, unknown>[]).find((section) =>
        String(section['segment_name'] ?? section['segmentName'] ?? '')
          .toLowerCase()
          .includes(name.toLowerCase()),
      );

    const zoneMatchers: Record<PipelineZone, string> = {
      mainLine: 'main',
      branchLine: 'branch',
      inEngineRoom: 'engine',
    };

    (Object.keys(zoneMatchers) as PipelineZone[]).forEach((zone) => {
      const section = byName(zoneMatchers[zone]);
      if (!section) {
        return;
      }

      const thicknessValues = normalizePipelineValueList(
        section['pipeline_thickness_values_mm'] ??
          section['pipeline_thickness_mm'] ??
          section['pipelineThicknessMm'] ??
          section['pipelineThicknessValuesMm'],
        PIPELINE_THICKNESS_FIELDS[zone].length,
      );
      const lengthValues = normalizePipelineValueList(
        section['length_values_m'] ??
          section['length_m'] ??
          section['lengthM'] ??
          section['lengthValuesM'],
        PIPELINE_LENGTH_FIELDS[zone].length,
      );

      PIPELINE_THICKNESS_FIELDS[zone].forEach((field, index) => {
        if (thicknessValues[index] != null) {
          patch[field.key] = thicknessValues[index];
          restored = true;
        }
      });
      PIPELINE_LENGTH_FIELDS[zone].forEach((field, index) => {
        if (lengthValues[index] != null) {
          patch[field.key] = lengthValues[index];
          restored = true;
        }
      });

      const insulationKey =
        zone === 'mainLine'
          ? 'steamInsulationMainLine'
          : zone === 'branchLine'
            ? 'steamInsulationBranchLine'
            : 'steamInsulationInEngineRoom';
      const insulation =
        section['steam_insulation'] ?? section['steamInsulation'];
      if (insulation != null && insulation !== '') {
        patch[insulationKey] = insulation;
        restored = true;
      }
    });
  }

  ALL_PIPELINE_DETAIL_FIELD_KEYS.forEach((key) => {
    const snakeKey = PIPELINE_FIELD_SNAKE_MAP[key];
    const rootValue = saved[key] ?? (snakeKey ? saved[snakeKey] : undefined);
    if (rootValue !== null && rootValue !== undefined && rootValue !== '') {
      patch[key] = rootValue;
      restored = true;
    }
  });

  Object.entries(LEGACY_PIPELINE_FIELD_MAP).forEach(([legacyKey, modernKey]) => {
    const legacyValue =
      saved[legacyKey] ??
      saved[
        legacyKey
          .replace(/([A-Z])/g, '_$1')
          .replace(/^_/, '')
          .toLowerCase()
      ];
    if (
      legacyValue !== null &&
      legacyValue !== undefined &&
      legacyValue !== '' &&
      patch[modernKey] == null
    ) {
      patch[modernKey] = legacyValue;
      restored = true;
    }
  });

  const legacyThickness = saved['pipelineThicknessMm'] ??
    saved['pipeline_thickness_mm'];
  const legacyLength = saved['pipelineLengthM'] ?? saved['pipeline_length_m'];
  if (legacyThickness != null && patch['pipelineMainLineThickness1Mm'] == null) {
    patch['pipelineMainLineThickness1Mm'] = legacyThickness;
    restored = true;
  }
  if (legacyLength != null && patch['pipelineMainLineLength1M'] == null) {
    patch['pipelineMainLineLength1M'] = legacyLength;
    restored = true;
  }

  if (
    saved['steamPipesInsulation'] != null ||
    saved['steam_pipes_insulation'] != null
  ) {
    const insulation =
      saved['steamPipesInsulation'] ?? saved['steam_pipes_insulation'];
    patch['steamInsulationMainLine'] ??= insulation;
    patch['steamInsulationBranchLine'] ??= insulation;
    patch['steamInsulationInEngineRoom'] ??= insulation;
    restored = true;
  }

  return restored ? patch : null;
}

export function sumPipelineZoneLengths(
  formValue: Record<string, unknown>,
  zone: PipelineZone,
): number | null {
  const total = PIPELINE_LENGTH_FIELDS[zone].reduce(
    (sum, field) => sum + (toOptionalNumber(formValue[field.key]) ?? 0),
    0,
  );
  return total > 0 ? Math.round(total * 100) / 100 : null;
}

export function sumPipelineLengths(
  formValue: Record<string, unknown>,
): number | null {
  const total = ALL_PIPELINE_DETAIL_FIELD_KEYS.filter((key) =>
    key.includes('Length'),
  ).reduce((sum, key) => sum + (toOptionalNumber(formValue[key]) ?? 0), 0);
  return total > 0 ? Math.round(total * 100) / 100 : null;
}

export function primaryPipelineThickness(
  formValue: Record<string, unknown>,
): number {
  return toOptionalNumber(formValue['pipelineMainLineThickness1Mm']) ?? 0;
}
